import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Briefcase, 
  Calendar,
  Loader2,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Users,
  LogOut,
  Home,
  Plus,
  Settings
} from 'lucide-react';
import { format } from 'date-fns';
import TenantMobileLayout from './TenantMobileLayout';

const TenantMobileDashboard = () => {
  const navigate = useNavigate();
  const { api, user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayJobs: 0,
    unallocatedJobs: 0,
    pendingConfirmations: 0,
    activeJobs: 0
  });
  const [todayJobs, setTodayJobs] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      // Fetch jobs, allocations, and customers
      const [jobsRes, allocationsRes, customersRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/allocations'),
        api.get('/customers')
      ]);

      const jobs = jobsRes.data || [];
      const allocations = allocationsRes.data || [];
      const customersData = customersRes.data || [];
      
      setCustomers(customersData);

      // Calculate stats
      const todayJobsList = jobs.filter(job => {
        const startDate = job.start_date;
        const endDate = job.end_date || job.start_date;
        return today >= startDate && today <= endDate;
      });

      const unallocated = jobs.filter(job => job.status === 'not_allocated');
      const allocated = jobs.filter(job => job.status === 'allocated');
      const confirmed = jobs.filter(job => job.status === 'confirmed');
      const activeJobs = jobs.filter(job => 
        ['not_allocated', 'allocated', 'confirmed'].includes(job.status)
      );

      setStats({
        todayJobs: todayJobsList.length,
        unallocatedJobs: unallocated.length,
        pendingConfirmations: allocated.length,
        activeJobs: activeJobs.length
      });

      setTodayJobs(todayJobsList.slice(0, 5)); // Show first 5
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || 'Unknown Customer';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <TenantMobileLayout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        </div>
      </TenantMobileLayout>
    );
  }

  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs">Welcome back</p>
            <h1 className="text-white font-bold text-lg">{user?.full_name || user?.username}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout} 
              className="text-gray-400"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card 
            className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
            onClick={() => navigate('/tenant-mobile/jobs?filter=today')}
            data-testid="today-jobs-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                  <Calendar className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.todayJobs}</p>
                  <p className="text-xs text-gray-400">Today's Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
            onClick={() => navigate('/tenant-mobile/jobs?filter=unallocated')}
            data-testid="unallocated-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.unallocatedJobs}</p>
                  <p className="text-xs text-gray-400">Unallocated</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
            onClick={() => navigate('/tenant-mobile/jobs?filter=allocated')}
            data-testid="pending-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pendingConfirmations}</p>
                  <p className="text-xs text-gray-400">To Confirm</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
            onClick={() => navigate('/tenant-mobile/jobs?filter=active')}
            data-testid="active-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.activeJobs}</p>
                  <p className="text-xs text-gray-400">Active Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            className="h-auto py-4 flex flex-col items-center gap-2 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
            onClick={() => navigate('/tenant-mobile/jobs/new')}
            data-testid="create-job-btn"
          >
            <Plus className="h-6 w-6" />
            <span className="text-sm font-medium">Create Job</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#3a3a3a]"
            onClick={() => navigate('/tenant-mobile/jobs')}
            data-testid="view-all-jobs-btn"
          >
            <Briefcase className="h-6 w-6" />
            <span className="text-sm font-medium">All Jobs</span>
          </Button>
        </div>

        {/* Today's Jobs Preview */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#ffd108]" />
              Today's Jobs
            </h2>
            <Badge variant="secondary" className="bg-[#ffd108] text-[#1a1a1a]">
              {stats.todayJobs}
            </Badge>
          </div>

          {todayJobs.length === 0 ? (
            <Card className="bg-[#2a2a2a] border-gray-700">
              <CardContent className="py-8 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">No jobs scheduled for today</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayJobs.map((job) => (
                <Card 
                  key={job.id}
                  className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
                  onClick={() => navigate(`/tenant-mobile/jobs/${job.id}`)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#ffd108] font-bold text-sm">
                            {job.start_time ? job.start_time.slice(0, 5) : 'TBD'}
                          </span>
                          <StatusBadge status={job.status} />
                        </div>
                        <h3 className="text-white font-medium truncate">{getCustomerName(job.customer_id)}</h3>
                        <p className="text-gray-400 text-sm truncate">{job.description}</p>
                        {job.equipment_type && (
                          <p className="text-gray-500 text-xs mt-1">{job.equipment_type}</p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {todayJobs.length > 0 && (
            <Button 
              variant="outline" 
              className="w-full mt-3 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
              onClick={() => navigate('/tenant-mobile/jobs?filter=today')}
            >
              View All Today's Jobs
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </TenantMobileLayout>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  // Status colors use CSS variables that change based on theme
  const baseClass = "border text-xs";
  switch (status) {
    case 'not_allocated':
      return <Badge variant="secondary" className={baseClass} style={{ backgroundColor: 'var(--status-not-allocated-bg)', color: 'var(--status-not-allocated-text)', borderColor: 'var(--status-not-allocated-border)' }}>Not Allocated</Badge>;
    case 'allocated':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-allocated-bg)', color: 'var(--status-allocated-text)', borderColor: 'var(--status-allocated-border)' }}>Allocated</Badge>;
    case 'confirmed':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-confirmed-bg)', color: 'var(--status-confirmed-text)', borderColor: 'var(--status-confirmed-border)' }}>Confirmed</Badge>;
    case 'docketed':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-docketed-bg)', color: 'var(--status-docketed-text)', borderColor: 'var(--status-docketed-border)' }}>Docketed</Badge>;
    case 'invoiced':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-invoiced-bg)', color: 'var(--status-invoiced-text)', borderColor: 'var(--status-invoiced-border)' }}>Invoiced</Badge>;
    default:
      return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  }
};

export default TenantMobileDashboard;
